import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone } from "lucide-react";
import { toast } from "sonner";

/**
 * Blocks the app behind a required-phone screen for any signed-in user whose
 * `phone` is missing — this catches OTP/passwordless accounts that were
 * auto-provisioned on first login (see /api/auth/otp/request) and never went
 * through Register.jsx, plus any older account that predates the phone
 * requirement. Shown once; disappears permanently once phone is saved.
 */
export default function RequirePhoneGate() {
  const { user, isAuthed, updatePhone } = useAuth();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const needsPhone = isAuthed && user && !user.phone;
  if (!needsPhone) return null;

  const submit = async (e) => {
    e.preventDefault();
    const p = phone.trim();
    if (!p) { toast.error("Enter a phone number to continue."); return; }
    setBusy(true);
    try {
      await updatePhone(p);
      toast.success("Phone number saved");
    } catch (err) {
      toast.error(err.message || "Could not save phone number");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[420px]" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Phone className="h-4 w-4" /> Add your phone number</DialogTitle>
          <DialogDescription>
            One quick step before you continue — technicians use this to reach you directly about your tickets.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div>
            <Label htmlFor="gate-phone">Phone</Label>
            <Input id="gate-phone" type="tel" autoFocus value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210" className="mt-1.5 rounded-xl" required />
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-full">
            {busy ? "Saving…" : "Continue"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
